import {initialsFromName} from '@app/helpers/name';
import {useState} from 'react';
import styled, {css} from 'styled-components';

export type AvatarSize = 'sm' | 'md';

export type AvatarProps = {
  name: string;
  imageUrl?: string | null;
  size?: AvatarSize;
  square?: boolean;
  className?: string;
};

const sizeStyles: Record<AvatarSize, ReturnType<typeof css>> = {
  sm: css`
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.5625rem;
  `,
  md: css`
    width: 2rem;
    height: 2rem;
    font-size: 0.6875rem;
  `,
};

const Wrapper = styled.div<Pick<AvatarProps, 'size' | 'square'>>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: 700;
  line-height: 1;
  background-color: var(--theme-contrast-color);
  color: var(--theme-on-contrast-color);
  ${({size = 'md'}) => sizeStyles[size]};
  ${({square}) =>
    square
      ? css`
          border-radius: var(--theme-surface-radius, 0.25rem);
        `
      : css`
          border-radius: 50%;
        `}
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const shouldShowImage = (url: string | null | undefined): url is string => {
  return Boolean(url && !url.includes('missing.png'));
};

const Avatar = ({
  name,
  imageUrl,
  size = 'md',
  square = false,
  className,
}: AvatarProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showPhoto =
    shouldShowImage(imageUrl ?? null) && !imageFailed && Boolean(imageUrl);

  const label = name.trim() || 'Avatar';

  return (
    <Wrapper
      size={size}
      square={square}
      className={className}
      role="img"
      aria-label={label}
    >
      {showPhoto ? (
        <Photo src={imageUrl!} alt="" onError={() => setImageFailed(true)} />
      ) : (
        initialsFromName(name || '?')
      )}
    </Wrapper>
  );
};

export default Avatar;
